import React, {
  useCallback,
  useEffect,
  useState,
  createContext,
  useContext,
} from 'react'
import { usePDF } from '@react-pdf/renderer'
import { TemplateSwitcher, useTemplate } from '~/lib/pdf'
import { apiPost, apiPut } from '~/util/api'
import { useToast } from '~/lib/ui/toast'
import { sha256 } from 'js-sha256'
import useTranslation from '~/components/shared/TranslationProvider'

const ResumeContext = createContext()

export const useResume = () => {
  return useContext(ResumeContext)
}

const hashObject = (obj) => {
  const json = JSON.stringify(obj)
  return sha256(json)
}

const ResumeProvider = ({
  resume_json,
  uncategorized_data: uncategorizedData,
  document_id: documentId,
  resume_name,
  docx_enabled: docxEnabled,
  dev_mode: devMode,
  children,
  locale,
}) => {
  const { t } = useTranslation()

  const [resumeDocument, setResumeDocument] = useState(resume_json)
  const [resumeName, setResumeName] = useState(resume_name)
  const [lastPersistedHash, setLastPersistedHash] = useState(
    hashObject(documentId ? resume_json : {}),
  )

  const { toast } = useToast()
  const { templateIndex, templateStyle, templates } = useTemplate()
  const [previewTemplateIndex, setPreviewTemplateIndex] = useState(
    documentId ? null : locale === 'en_gb' ? 2 : templateIndex,
  )

  const [lastSettingsHash, setLastSettingsHash] = useState(
    hashObject(documentId ? templateStyle : {}),
  )

  const documentChanged = hashObject(resumeDocument) !== lastPersistedHash
  const settingsChanged = hashObject(templateStyle) !== lastSettingsHash
  const documentOrSettingsChanged = documentChanged || settingsChanged

  useEffect(() => {
    // If the resume has unsaved changes, warn the user before leaving the page
    if (documentOrSettingsChanged) {
      window.onbeforeunload = (event) => {
        event.preventDefault()
        event.returnValue = true // For legacy browser compatibility
      }
    } else {
      window.onbeforeunload = undefined
    }
  }, [documentOrSettingsChanged])

  // use hook for fine-grained control over PDF rendering
  const [pdf, renderPdf] = usePDF({
    document: (
      <TemplateSwitcher
        jsonDocument={resumeDocument}
        templateIndex={templateIndex}
        templateStyle={templateStyle}
        title={resumeName}
        Template={templates[templateIndex]}
        locale={locale}
      />
    ),
  })

  // Re-render the PDF
  const updateResume = useCallback(() => {
    renderPdf(
      <TemplateSwitcher
        jsonDocument={resumeDocument}
        templateIndex={templateIndex}
        templateStyle={templateStyle}
        title={resumeName}
        Template={templates[templateIndex]}
        locale={locale}
      />,
    )
    //don't run on resumeName change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    resumeDocument,
    renderPdf,
    templateIndex,
    templateStyle,
    templates,
    locale,
  ])

  // Automatically re-render pdf when screen is resized
  useEffect(() => {
    window.addEventListener('resize', updateResume)

    return () => window.removeEventListener('resize', updateResume)
  }, [updateResume])

  // Automatically re-render pdf every time resumeDocument changes
  useEffect(() => {
    updateResume()
  }, [resumeDocument, updateResume])

  // Persist resume data to the server
  const persistResume = async (allowRedirect = true) => {
    setLastPersistedHash(hashObject(resumeDocument))
    setLastSettingsHash(hashObject(templateStyle))
    if (documentId) {
      await saveExistingResume()
    } else {
      await saveNewResume(allowRedirect)
    }
  }

  const persistResumeIfChanged = async () => {
    const documentChanged = hashObject(resumeDocument) !== lastPersistedHash
    const settingsChanged = hashObject(templateStyle) !== lastSettingsHash
    if (documentChanged || settingsChanged) {
      await persistResume()
    }
  }

  const saveNewResume = async (allowRedirect = true) => {
    let response
    try {
      response = await apiPost('/resume/builder/', {
        resume: resumeDocument,
        template_index: templateIndex,
        template_settings: templateStyle,
        name: resumeName,
      })
    } catch (e) {
      console.error(
        'Error saving new resume. Expected status 200. Got:',
        e.response.status,
        e.response,
      )
      toast({ description: t('failed_to_save_resume') })
      return
    }
    if (response.status === 201) {
      allowRedirect &&
        (window.location.href = '/resume/builder/' + response.data.document_id)
    } else {
      console.error(
        'Error saving new resume. Expected status 200. Got:',
        response.status,
        response,
      )
      toast({ description: t('failed_to_create_resume') })
    }
  }

  const saveExistingResume = async () => {
    let response
    toast({ description: t('saving_resume') })
    try {
      response = await apiPut(`/resume/builder/${documentId}`, {
        resume: resumeDocument,
        template_index: templateIndex,
        template_settings: templateStyle,
        name: resumeName,
      })
    } catch (e) {
      console.error(
        'Error saving existing resume. Expected status 200. Got:',
        e.response.status,
        e.response,
      )
      toast({ description: t('failed_to_save_resume') })
      return
    }
    if (response.status === 200) {
      toast({ description: t('success_resume_saved'), duration: 1500 })
    } else {
      console.error(
        'Error saving existing resume. Expected status 200. Got:',
        response.status,
        response,
      )
      toast({ description: t('failed_to_save_resume') })
    }
  }

  const value = {
    pdf,
    documentId,
    persistResume,
    persistResumeIfChanged,
    resumeDocument,
    setResumeDocument,
    resumeName,
    setResumeName,
    previewTemplateIndex,
    setPreviewTemplateIndex,
    docxEnabled,
    uncategorizedData,
    devMode,
    documentOrSettingsChanged,
    locale,
  }

  return (
    <ResumeContext.Provider value={value}>{children}</ResumeContext.Provider>
  )
}
export default ResumeProvider
